import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopBody, WorkshopDetailDTO } from "../../../../models/WorkshopDTO";
import S from "./strings";
import { Modal, Opciones, conMoneda, errorText, numOrNull, usePlantel } from "./comun";
import DiasEditor, { DiaDraft, diaValido } from "./dias";
import { cursoCorto } from "../../../../utils/cursoCorto";

interface Props {
	campusId: number;
	opciones: Opciones;
	taller: WorkshopDetailDTO | null;
	onClose: () => void;
	onSaved: (t: WorkshopDetailDTO, texto: string) => void;
}

/**
 * Crear / editar un taller. Al crear se eligen sus días de una vez (v2, 2026-10-23); después los días se cambian en Sesiones.
 * El histórico no llega aquí.
 */
const WorkshopForm: React.FC<Props> = ({ campusId, opciones, taller, onClose, onSaved }) => {
	// R2: etiquetas de dinero con la moneda (la del taller o la del plantel al crear)
	const plantel = usePlantel();
	const cur = taller?.currency || plantel.currency;
	const http = HttpManager.getInstance();
	const cursosDelTaller = (taller?.courses?.length ? taller.courses : (taller?.courseId ? [{ id: taller.courseId, name: taller.courseName || "" }] : []))
		.map(c => ({ id: c.id, name: cursoCorto(c.name) || c.name }));
	const [f, setF] = useState({
		name: taller?.name || "",
		courseIds: cursosDelTaller.map(c => c.id),
		mentorHourlyRate: taller?.mentorHourlyRate != null ? String(taller.mentorHourlyRate) : "",
		capacity: taller?.capacity != null ? String(taller.capacity) : "",
		price: taller ? String(taller.price ?? "") : "",
		notes: taller?.notes || "",
		status: (taller?.status === "historico" ? "programado" : taller?.status || "programado") as "programado" | "realizado" | "anulado"
	});
	const [dias, setDias] = useState<DiaDraft[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));
	const diasOk = taller !== null || (dias.length > 0 && dias.every(diaValido));
	const valido = f.name.trim().length > 1 && f.price.trim() !== "" && !isNaN(Number(f.price)) && diasOk;

	//Cursos que ya traía el taller aunque hoy no tengan horarios activos.
	const cursos = [...opciones.cursos];
	cursosDelTaller.forEach(c => { if (!cursos.some(x => x.id === c.id)) cursos.unshift(c); });
	const alternarCurso = (id: number) => set("courseIds", f.courseIds.includes(id) ? f.courseIds.filter(x => x !== id) : [...f.courseIds, id]);

	const guardar = () => {
		if (!valido) { if (!diasOk) setError(S.faltanDias); return; }
		const body: WorkshopBody = {
			name: f.name.trim(), price: Number(f.price), courseIds: f.courseIds, mentorHourlyRate: numOrNull(f.mentorHourlyRate),
			capacity: numOrNull(f.capacity), notes: f.notes.trim() || null
		};
		if (!taller) {
			body.sessions = dias.map(d => ({ date: d.date, startTime: d.startTime, endTime: d.endTime || null, classroomId: d.classroomId || null, mentorId: d.mentorId || null }));
		}
		setBusy(true); setError(null);
		const req = taller ? http.workshopsService.update(campusId, { ...body, id: taller.id, status: f.status }) : http.workshopsService.create(campusId, body);
		req.then(r => onSaved(r.data, taller ? S.guardado : S.creado))
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};

	return (
		<Modal title={taller ? S.editarTaller : S.nuevo} onClose={onClose} ancho={taller ? 680 : 1120}>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "2 1 260px" }}><span className="wau-lab">{S.nombre}</span>
					<input className="wau-entrada" value={f.name} onChange={e => set("name", e.target.value)} autoFocus /></label>
				{taller ? (
					<label className="wau-campo" style={{ flex: "1 1 150px" }}><span className="wau-lab">{S.estado}</span>
						<select className="wau-entrada" value={f.status} onChange={e => set("status", e.target.value)}>
							<option value="programado">{S.estados.programado}</option>
							<option value="realizado">{S.estados.realizado}</option>
							<option value="anulado">{S.estados.anulado}</option>
						</select></label>
				) : null}
				<label className="wau-campo" style={{ flex: "1 1 120px" }}><span className="wau-lab">{conMoneda(S.precio, cur)}</span>
					<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={f.price} onChange={e => set("price", e.target.value)} /></label>
				<label className="wau-campo" style={{ flex: "1 1 90px" }}><span className="wau-lab">{S.cupoMax}</span>
					<input type="number" min={1} className="wau-entrada wau-num" value={f.capacity} onChange={e => set("capacity", e.target.value)} /></label>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{conMoneda(S.tarifaHora, cur)}</span>
					<input type="number" min={0} step="0.01" className="wau-entrada wau-num" placeholder={S.tarifaHoraPh} value={f.mentorHourlyRate} onChange={e => set("mentorHourlyRate", e.target.value)} /></label>
			</div>
			{taller && f.status === "anulado" && taller.status !== "anulado" ? <p className="wau-adv">{S.anularAviso}</p> : null}
			<div className="wau-campo" style={{ marginBottom: 12 }}><span className="wau-lab">{S.cursos}</span>
				<div className="wte-cursos" role="group" aria-label={S.cursos}>
					{cursos.map(c => <button key={c.id} type="button" className="wau-chip" aria-pressed={f.courseIds.includes(c.id)} onClick={() => alternarCurso(c.id)}>{c.name}</button>)}
				</div>
			</div>
			{taller ? null : <>
				<span className="wau-lab">{S.dias}</span>
				<div style={{ margin: "4px 0 12px 0" }}><DiasEditor value={dias} onChange={setDias} opciones={opciones} /></div>
			</>}
			<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
				<textarea className="wau-entrada" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></label>
			{error ? <p className="wau-adv" role="alert" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{taller ? S.guardar : S.crear}</button>
			</div>
		</Modal>
	);
};

export default WorkshopForm;
